import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowRight, Plus, Shield, FileText, Zap, Mail, Camera, Award, ExternalLink, Share2, Search, Users } from 'lucide-react';
import { ShareCertificateDialog } from '@/components/account/ShareCertificateDialog';

const STATUS_LABELS = {
  order_received: 'Order Received', in_review: 'In Review',
  more_images_requested: 'More Images Requested', images_received: 'Images Received',
  authentic: 'Authentic', counterfeit: 'Counterfeit',
  appraisal_complete: 'Appraisal Complete', unable_to_determine: 'Unable to Determine',
  refunded: 'Refunded', revoked: 'Revoked', paid: 'Received',
};
const STATUS_COLORS = {
  authentic: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  counterfeit: 'bg-red-50 text-red-700 border-red-200',
  more_images_requested: 'bg-orange-50 text-orange-700 border-orange-200',
  refunded: 'bg-red-50 text-red-600 border-red-200',
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareTarget, setShareTarget] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/orders').then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const q = search.trim().toLowerCase();
  const matches = (o) => {
    if (!q) return true;
    if ((o.order_number || '').toLowerCase().includes(q)) return true;
    return (o.items || []).some(i =>
      (i.item_title || '').toLowerCase().includes(q) ||
      (i.brand_name || '').toLowerCase().includes(q) ||
      (i.category_name || '').toLowerCase().includes(q));
  };
  const displayOrders = orders
    .filter(o => o.status !== 'pending' && o.status !== 'cancelled')
    .filter(matches)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl font-medium text-ink mb-1" data-testid="account-orders-title">My Orders</h1>
          <p className="text-sm text-muted-foreground">View and track all your orders.</p>
        </div>
        <Link to="/order/submit"><Button size="sm" className="gap-1.5 rounded-[12px]"><Plus className="h-3.5 w-3.5" /> New Order</Button></Link>
      </div>

      <div className="relative mb-4">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by brand, title, category or order #..."
          className="w-full pl-9 pr-3 py-2 rounded-[12px] border border-stone bg-white text-sm focus:outline-none focus:ring-1 focus:ring-ring/30"
          data-testid="orders-search-input" />
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-stone/20 rounded-lg" />)}</div>
      ) : displayOrders.length === 0 ? (
        <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><ShoppingBag className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No orders yet.</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {displayOrders.map(order => {
            const items = order.items || [];
            const needsAction = items.some(i => i.status === 'more_images_requested');
            return (
              <Link key={order.id} to={`/account/orders/${order.id}`} className="block no-underline">
                <Card className={`bg-white border-stone/50 hover:shadow-[var(--shadow-pop)] transition-shadow duration-200 ${needsAction ? 'border-l-4 border-l-orange-400' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground">{order.order_number}</span>
                          <span className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</span>
                          {order.account_email && (
                            <Badge variant="outline" className="text-[8px] text-muted-foreground" data-testid="grouped-order-badge">
                              <Users className="h-2 w-2 mr-0.5" /> {order.account_email}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-ink mt-0.5">${(order.total_cents / 100).toFixed(2)}</p>
                      </div>
                      {needsAction && <Badge className="text-[9px] bg-orange-50 text-orange-700 border-orange-200"><Camera className="h-2.5 w-2.5 mr-0.5" /> Action Needed</Badge>}
                      <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                    </div>
                    {/* Item details */}
                    <div className="space-y-1">
                      {items.slice(0, 3).map((item, i) => (
                        <div key={i}>
                          <div className="flex items-center gap-2 text-xs">
                            {item.service_family === 'authentication' ? <Shield className="h-3 w-3" style={{color:'#0E6B5D'}} /> : <FileText className="h-3 w-3" style={{color:'#A36A3A'}} />}
                            <span className="text-ink truncate max-w-[200px]">{item.item_title || 'Untitled'}</span>
                            {item.brand_name && <span className="text-muted-foreground">{item.brand_name}</span>}
                            <Badge variant="outline" className={`text-[8px] ml-auto ${STATUS_COLORS[item.status] || ''}`}>
                              {STATUS_LABELS[item.status] || item.status}
                            </Badge>
                          </div>
                          {item.certificate_code && !item.certificate_revoked && (
                            <div className="flex items-center gap-2 mt-1 ml-5">
                              <Award className="h-3 w-3" style={{ color: '#0E6B5D' }} />
                              <span className="font-mono text-[10px] text-muted-foreground uppercase">{item.certificate_code}</span>
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
                                data-testid={`view-certificate-${item.id}`}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/verify?code=${item.certificate_code}`, '_blank'); }}>
                                <ExternalLink className="h-2.5 w-2.5" /> View Certificate
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
                                data-testid={`share-certificate-${item.id}`}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareTarget({ itemId: item.id, certCode: item.certificate_code, itemTitle: item.item_title }); }}>
                                <Share2 className="h-2.5 w-2.5" /> Share
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      {items.length > 3 && <p className="text-[10px] text-muted-foreground">+{items.length - 3} more items</p>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
      <ShareCertificateDialog target={shareTarget} onClose={() => setShareTarget(null)} />
    </div>
  );
}
